import React from "react"
import ReactDOM from "react-dom/client"
import { ChakraProvider, defaultSystem } from "@chakra-ui/react"
import { BrowserRouter } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import * as Sentry from "@sentry/react"
import App from "./App.tsx"
import { FirebaseAuthProvider } from "./firebase/auth-context"
import { initSentry } from "./lib/sentry"

initSentry()

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{ padding: "2rem", fontFamily: "system-ui" }}>
          <h1>Something went wrong</h1>
          <p style={{ color: "#666", marginBottom: "1rem" }}>
            {(error as Error).message}
          </p>
          {(error as Error).cause ? (
            <details style={{ marginBottom: "1rem" }}>
              <summary>Error details</summary>
              <pre style={{ background: "#f5f5f5", padding: "1rem", overflow: "auto" }}>
                {String((error as Error).cause)}
              </pre>
            </details>
          ) : null}
          <button
            onClick={resetError}
            style={{
              padding: "0.5rem 1rem",
              background: "#007AFF",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      )}
      beforeCapture={(scope, _error, errorInfo) => {
        scope.setTag("error.boundary", "root");
        scope.setContext("react_error_info", errorInfo as unknown as Record<string, unknown>);
      }}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <FirebaseAuthProvider>
            <ChakraProvider value={defaultSystem}>
              <App />
              <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
            </ChakraProvider>
          </FirebaseAuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
)
